// applications/_shared/services/upload — the three-call document-upload sequence, shared by
// `dossier` (the draft wizard) and `respond` (fulfilling a coordinator's information request).
// Pure async functions only — no React, no hooks, no state.
import { privateApi } from "@core/libs/axios";
import { parseWith } from "@core/parse";
import {
  DocumentCompleteSchema,
  UploadIntentSchema,
  type DocumentComplete,
  type UploadIntent,
} from "../types/upload";

/**
 * SHA-256 of a file's bytes, hex-encoded — computed on-device because the upload-intent request
 * has to declare it BEFORE any byte reaches the server (`checksum_sha256` is a required field on
 * `UploadIntentSerializer`, and `complete_document_upload` verifies it against the object the
 * server actually received). `crypto.subtle` needs a secure context — true for `https:` in
 * production and for `localhost` in dev, both of which this app runs under.
 */
async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * `POST /acquire/applications/{id}/documents/upload-intents/` — the first of three calls in the
 * upload sequence (intent → direct-to-storage POST → complete). Computes the checksum itself so
 * every caller sends the same thing the server will later verify against.
 *
 * `informationRequestId`, when passed, is what lets this succeed AFTER submission — the server's
 * own per-field edit scoping (`_require_information_request_scope`) refuses any post-submission
 * document write that isn't attached to a matching OPEN information request. Omit it for the
 * draft-wizard case, where the whole dossier is still editable and no request is in play.
 */
export async function createUploadIntent(
  applicationId: string,
  expectedRevision: number,
  requirementId: string,
  file: File,
  informationRequestId?: string
): Promise<UploadIntent> {
  const checksum = await sha256Hex(file);
  const dto = await privateApi
    .post<unknown>(`/acquire/applications/${applicationId}/documents/upload-intents/`, {
      expected_revision: expectedRevision,
      requirement_id: requirementId,
      filename: file.name,
      mime_type: file.type,
      size_bytes: file.size,
      checksum_sha256: checksum,
      ...(informationRequestId
        ? { information_request_id: informationRequestId }
        : {}),
    })
    .then((r) => r.data);

  return parseWith(
    UploadIntentSchema,
    dto,
    "POST /applications/{id}/documents/upload-intents/"
  );
}

/**
 * POSTs the file directly to object storage using the presigned form `createUploadIntent`
 * returned. Deliberately a bare `fetch`, not `privateApi`: this request goes to a DIFFERENT
 * origin (the storage endpoint, not this app's `/api/v1/`), and `privateApi`/`api` both set
 * `withCredentials: true` for THIS app's own cookie — sending that cross-origin would be a
 * pointless credential leak, and MinIO's CORS policy for this endpoint has no reason to accept it.
 * The presigned fields ARE the auth; nothing else is needed.
 *
 * `fields` must be appended before `file` — S3-compatible presigned POSTs read the file as the
 * end of the multipart body, and `FormData` preserves insertion order.
 */
async function uploadToPresignedPost(
  upload: { url: string; fields: Record<string, string> },
  file: File
): Promise<void> {
  const body = new FormData();
  for (const [key, value] of Object.entries(upload.fields)) {
    body.append(key, value);
  }
  body.append("file", file);

  const response = await fetch(upload.url, { method: "POST", body });
  if (!response.ok) {
    throw new Error(
      `Upload to storage failed with status ${String(response.status)}`
    );
  }
}

/**
 * `POST /acquire/applications/{id}/documents/{documentId}/complete/` — the third call in the
 * sequence. The server re-fetches the object from storage and verifies size + checksum before
 * marking it `AVAILABLE`; a file that never fully landed, or landed corrupted, comes back
 * `409 document_upload_incomplete` here rather than silently passing.
 *
 * ⚠️ Same narrow response shape as the intent call — not the full DTO.
 */
export async function completeDocumentUpload(
  applicationId: string,
  documentId: string,
  expectedRevision: number,
  informationRequestId?: string
): Promise<DocumentComplete> {
  const dto = await privateApi
    .post<unknown>(
      `/acquire/applications/${applicationId}/documents/${documentId}/complete/`,
      {
        expected_revision: expectedRevision,
        ...(informationRequestId
          ? { information_request_id: informationRequestId }
          : {}),
      }
    )
    .then((r) => r.data);

  return parseWith(
    DocumentCompleteSchema,
    dto,
    "POST /applications/{id}/documents/{documentId}/complete/"
  );
}

/**
 * Runs the full three-call sequence — intent, direct-to-storage upload, complete — and returns
 * once the document is confirmed `AVAILABLE` (or throws on any step).
 *
 * ⚠️ `getExpectedRevision()` is called again before `complete`, but that is NOT enough on its own
 * — the intent call ALREADY bumped the server's revision (its own response carries the new
 * value), and whatever `getExpectedRevision` reads from (a query cache, typically) has no way to
 * know that unless something told it. That is what `onIntentSucceeded` is for: the caller MUST
 * write `intent.application_revision` into wherever `getExpectedRevision` reads from before
 * returning, or the `complete` call below reads the SAME stale value the intent call did and
 * 409s every single time — verified live, this was exactly the bug the first version of this
 * function shipped with.
 */
export async function uploadDocument(
  applicationId: string,
  requirementId: string,
  file: File,
  getExpectedRevision: () => number,
  onIntentSucceeded: (newRevision: number) => void,
  informationRequestId?: string
): Promise<DocumentComplete> {
  const intent = await createUploadIntent(
    applicationId,
    getExpectedRevision(),
    requirementId,
    file,
    informationRequestId
  );
  onIntentSucceeded(intent.application_revision);
  await uploadToPresignedPost(intent.upload, file);
  return completeDocumentUpload(
    applicationId,
    intent.document_id,
    getExpectedRevision(),
    informationRequestId
  );
}
